import { useMemo } from 'react';

import { gql, useQuery } from '@apollo/client';

import type {
  ProgramBrowsingExperiencesV1MultiGetQuery,
  ProgramBrowsingExperiencesV1MultiGetQueryVariables,
} from 'bundles/naptimejs/resources/__generated__/ProgramBrowsingExperiencesV1';
import { BrowsingExperienceType } from 'bundles/naptimejs/resources/programBrowsingExperiences.v1';
import { useResolvedProgramIds } from 'bundles/unified-common/contexts/org-scoped/OrgScopedImpersonationContext';

/* eslint-disable graphql/template-strings */
const ProgramBrowsingExperiencesMultiGetQuery = gql`
  query ProgramBrowsingExperiencesMultiGet($ids: String!) {
    ProgramBrowsingExperiencesV1 @naptime {
      multiGet(ids: $ids) {
        elements {
          id
          browsingExperience
        }
      }
    }
  }
`;
/* eslint-enable graphql/template-strings */

/**
 * Detects whether all resolved programs use limited content (SMALL_SUBSET browsing experience).
 * Auth-agnostic — callers decide how to act on the result based on user state.
 *
 * Uses the resolved program IDs from the Program Resolver API (already fetched by
 * OrgScopedImpersonationDataProvider and shared via context) to avoid an extra network request,
 * then checks their browsing experience via ProgramBrowsingExperiencesV1.multiGet.
 */
const useIsOrgAllLimitedContent = (): { isAllLimitedContent: boolean; isLoading: boolean } => {
  const { programIds, loading: programsLoading } = useResolvedProgramIds();

  const { data: browsingData, loading: browsingLoading } = useQuery<
    ProgramBrowsingExperiencesV1MultiGetQuery,
    ProgramBrowsingExperiencesV1MultiGetQueryVariables
  >(ProgramBrowsingExperiencesMultiGetQuery, {
    variables: { ids: programIds.join(',') },
    skip: programIds.length === 0,
  });

  const isAllLimitedContent = useMemo(() => {
    const experiences = browsingData?.ProgramBrowsingExperiencesV1?.multiGet?.elements;
    if (!experiences || experiences.length === 0) return false;
    return experiences.every((el) => el.browsingExperience === BrowsingExperienceType.SMALL_SUBSET);
  }, [browsingData]);

  return {
    isAllLimitedContent,
    isLoading: programsLoading || browsingLoading,
  };
};

export default useIsOrgAllLimitedContent;
